// Keep individual files below common PHP 2 MiB limits, and batches below 8 MiB POST limits.
export const REPORT_PHOTO_MAX_BYTES = 1.5 * 1024 * 1024;
const BATCH_MAX_BYTES = 6 * 1024 * 1024;
const MAX_EDGE = 2048;

export async function prepareReportPhoto(file: File, maxBytes = REPORT_PHOTO_MAX_BYTES): Promise<File> {
  const supported = /^(image\/(jpeg|png|webp))$/i.test(file.type);
  if (supported && file.size <= maxBytes) return file;

  const url = URL.createObjectURL(file);
  const image = new Image();
  const canvas = document.createElement("canvas");
  try {
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error(`Could not read ${file.name}. Try choosing a JPEG or PNG picture.`)), 30000);
      image.onload = () => { window.clearTimeout(timer); resolve(); };
      image.onerror = () => { window.clearTimeout(timer); reject(new Error(`Could not read ${file.name}. Try choosing a JPEG or PNG picture.`)); };
      image.src = url;
    });
    if (!image.naturalWidth || !image.naturalHeight) throw new Error(`Could not read ${file.name}. Try another picture.`);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not prepare this picture. Please try another browser.");
    let scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
    for (let attempt = 0; attempt < 5; attempt++) {
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      for (const quality of [0.85, 0.72, 0.6]) {
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(value => value ? resolve(value) : reject(new Error("Could not prepare this picture. Try a JPEG or PNG picture.")), "image/jpeg", quality);
        });
        if (blob.size <= maxBytes) {
          const name = `${file.name.replace(/\.[^.]+$/, "") || "picture"}.jpg`;
          return new File([blob], name, { type: "image/jpeg", lastModified: file.lastModified });
        }
      }
      scale *= 0.75;
    }
    throw new Error(`${file.name} is too large to upload. Choose a smaller picture or fewer pictures at once.`);
  } finally {
    image.onload = null;
    image.onerror = null;
    image.src = "";
    canvas.width = 0;
    canvas.height = 0;
    URL.revokeObjectURL(url);
  }
}

export async function prepareReportPhotos(files: File[]): Promise<File[]> {
  if (!files.length || files.length > 20) throw new Error("Choose between 1 and 20 pictures at a time.");
  const maxBytes = Math.min(REPORT_PHOTO_MAX_BYTES, Math.floor(BATCH_MAX_BYTES / files.length));
  const prepared: File[] = [];
  // Sequential decoding avoids holding several large iPhone images in memory at once.
  for (const file of files) prepared.push(await prepareReportPhoto(file, maxBytes));
  return prepared;
}
